import { create } from 'zustand';
import { Product } from '../../models/DB';
import { createJSONStorage, persist } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getPrefixedStorageName } from '../../utils/storagePrefix';

export interface Categories {
	category: string;
	totalProducts: number;
	totalStock: number;
}

// Lightweight product data for caching (excludes heavy fields like full images)
interface CachedProduct {
	id: string;
	name: string;
	category: string;
	subcategory?: string;
	price: number;
	discount: number;
	discountType?: 'percentage' | 'value';
	inStock: boolean;
	isActive?: boolean;
	showRating?: boolean;
	thumbnailUrl?: string;
	thumbnailResizeMode?: string;
}

// Cache duration: 30 minutes
const CACHE_DURATION_MS = 30 * 60 * 1000;

type State = {
	allProducts: Product[];
	allCategories: Categories[];
	lowStockThreshold: number;
	// Lightweight cache for product list (survives refresh)
	cachedProducts: CachedProduct[];
	cacheTimestamp: number | null;
};

type Action = {
	updateProducts: (data: State['allProducts']) => void;
	setLowStockThreshold: (threshold: number) => void;
	isCacheValid: () => boolean;
	getCachedProductsByCategory: (category: string, excludeId?: string) => CachedProduct[];
};

// Helper to extract lightweight cache data from full product
const toCachedProduct = (product: Product): CachedProduct => ({
	id: product.id,
	name: product.name,
	category: product.category,
	subcategory: product.subcategory,
	price: product.price,
	discount: product.discount,
	discountType: product.discountType,
	inStock: product.inStock,
	isActive: product.isActive,
	showRating: product.showRating,
	thumbnailUrl: (product.images as any)?.thumbnail?.url,
	thumbnailResizeMode: (product.images as any)?.thumbnail?.resizeMode || product.thumbnailResizeMode
});

export const productStore = create(
	persist<State & Action>(
		(set, get) => ({
			allProducts: [],
			allCategories: [],
			lowStockThreshold: 30,
			cachedProducts: [],
			cacheTimestamp: null,

			setLowStockThreshold: (threshold: number) => set(() => ({ lowStockThreshold: threshold })),

			isCacheValid: () => {
				const { cacheTimestamp, cachedProducts } = get();
				if (!cacheTimestamp || cachedProducts.length === 0) return false;
				return Date.now() - cacheTimestamp < CACHE_DURATION_MS;
			},

			getCachedProductsByCategory: (category: string, excludeId?: string) => {
				const { cachedProducts } = get();
				return cachedProducts.filter(
					p => p.category === category && p.id !== excludeId && p.isActive !== false
				);
			},

			updateProducts: (data: Product[]) => {
				// Remove duplicate products 
				const uniqueProducts = data.filter(
					(product, index, self) => index === self.findIndex((p) => p.id === product.id)
				);

				const groupedProducts = uniqueProducts.reduce((acc, product) => {
					const { category } = product;
					if (!acc[category]) {
						acc[category] = [];
					}
					acc[category].push(product);
					return acc;
				}, {} as Record<string, Product[]>);

				const categories = Object.entries(groupedProducts).map(([category, categoryProducts]) => {
					const totalStock = categoryProducts.reduce(
						(total, product) => total + product.stockOnHand,
						0
					);
					const totalProducts = categoryProducts.length;
					return {
						category,
						totalProducts,
						totalStock,
					};
				});

				// Add an "All" category to the list of categories at the first index
				const allStock = uniqueProducts.reduce((total, product) => total + product.stockOnHand, 0);
				categories.unshift({ category: 'All', totalProducts: uniqueProducts.length, totalStock: allStock });

				// Create lightweight cache for persistence
				const cachedProducts = uniqueProducts.map(toCachedProduct);

				set((state) => ({
					allProducts: uniqueProducts,
					allCategories: categories,
					lowStockThreshold: state.lowStockThreshold,
					cachedProducts,
					cacheTimestamp: Date.now()
				}));
			},
		}),
		{
			name: getPrefixedStorageName('product-storage'),
			storage: createJSONStorage(() => AsyncStorage),
			// Persist lightweight cache only (not full products)
			partialize: (state) => ({
				lowStockThreshold: state.lowStockThreshold,
				cachedProducts: state.cachedProducts,
				cacheTimestamp: state.cacheTimestamp
			} as any),
		}
	)
);
